<script>
    // Written from what the code does, not from a template. Every claim here
    // was traced through flask-app/app.py and db/models before it was written:
    //
    //   /api/face_analyzer streams the selfie to the analyzer and returns the
    //   result. It writes nothing to the database and nothing to storage.
    //   The annotated copy is put in localStorage by the browser.
    //
    //   progresspictures rows hold pictureurl, analysisresults and datetaken.
    //   save_picture_to_storage tries GCS, and on Railway there are no GCP
    //   credentials, so it falls back to a base64 data URL stored in the row.
    //
    //   users holds age, skintype, skinareasofimprovement, location, allergies.
    //   recommendationclicks holds product, brand, price, rank and time, for
    //   signed-out visitors too.
    //
    //   Deletion is /api/account/delete in flask-app/app.py. The list below is
    //   the list of tables that endpoint touches, and the kept-but-unlinked
    //   paragraph is recommendationclicks and externalproductcandidates having
    //   their userid set to NULL. Apple accepts anonymising rather than
    //   deleting only where the policy says it happens, so it says it.
    //
    // The anchor ids below are linked directly from the footer and the app.
    export let updated = '9 October 2026';
</script>

<div class="u-page">
    <article class="u-prose">
        <p class="eyebrow">Legal</p>
        <h1>Privacy Policy</h1>
        <p class="updated">Last updated {updated}</p>

        <p class="lead">
            SkinGenie reads a photo of your face to work out what your skin needs.
            That is about as personal as data gets, so this page says plainly what
            happens to it. No part of it is written to be clever.
        </p>

        <h2 id="photos">Your face photo</h2>
        <p>
            When you take or upload a photo for analysis, it is sent to SkinGenie
            once, analysed, and the findings are returned to your device.
            <strong>The photo itself is not saved on our servers.</strong> It is not
            written to our database and it is not put in a storage bucket. It exists
            in memory for as long as the analysis takes, and then it is gone.
        </p>
        <p>
            The marked-up copy you see on the results screen is held by your browser,
            on your device, in local storage. Clearing your browser data removes it.
            The original full-resolution photo is deliberately not kept there either,
            so it cannot sit on a shared computer after you have finished.
        </p>

        <h2>Progress photos</h2>
        <p>
            Progress photos are different, because the point of them is to still be
            there next month. If you save one, we store the image, the date, and the
            analysis that went with it, against your account. On our current hosting
            the image is stored inside our database rather than in a separate
            storage bucket.
        </p>

        <h2>Your skin profile</h2>
        <p>
            What you tell us about your skin is stored against your account: the
            concerns you want worked on, your skin type, your age, the ingredients
            you say you react to, and how many steps you want in a routine. Age and
            sensitivities both change what you are shown, which is why they are
            asked for rather than guessed.
        </p>

        <h2>What you do with the recommendations</h2>
        <p>
            When you open a recommended product we record which product it was, its
            brand, its price, and where it sat in the list we showed you. That is how
            we find out when the ranking is wrong. It is recorded for signed-out
            visitors too, against a random identifier rather than a name.
        </p>

        <h2>Your account</h2>
        <p>
            Sign-in is handled by Auth0, who hold your sign-in details. We hold the
            identifier they give us.
        </p>

        <h2>Who else sees any of it</h2>
        <ul>
            <li>
                <strong>Auth0</strong> handles sign-in and holds your credentials.
            </li>
            <li>
                <strong>OpenAI</strong> sees a product photo, and only a product
                photo, when you use the scanner to identify something from a picture
                of a label or a screenshot. Your face photo is never sent there.
            </li>
            <li>
                <strong>Firecrawl</strong> fetches public retailer pages so we can
                read ingredient lists. It receives the address of a product page, not
                anything about you.
            </li>
            <li>
                <strong>Railway</strong> hosts the service and the database.
            </li>
        </ul>
        <p>
            Nothing is sold. Nothing is shared with brands, retailers or advertisers.
            There is no advertising on SkinGenie and no third-party tracking or
            analytics script in the product.
        </p>

        <h2 id="deletion">Deleting your data</h2>
        <p>
            <a href="/delete-account">Delete your account</a> from inside the app,
            whenever you want. It happens immediately, there is no grace period, and
            nobody has to approve it.
        </p>
        <p>These are deleted outright, and cannot be recovered:</p>
        <ul>
            <li>Your skin profile: concerns, skin type, age, and the ingredients you avoid</li>
            <li>Every face analysis on your account, and the progress photos saved with them</li>
            <li>The routine you built, including products you added by hand</li>
            <li>The products we had recommended and saved for you</li>
            <li>Your sign-in record at Auth0, so signing in again starts you from nothing</li>
            <li>Everything held on your own device, including the marked-up analysis photo</li>
        </ul>
        <p>
            One thing is kept rather than deleted, and this is the part worth reading
            twice. The record of which products were opened from a set of
            recommendations, and where each one sat in the list, stays. What is
            removed from it is the identifier: those rows no longer point at you, at
            your account, or at each other. We keep them because they are the only
            way we find out that our ranking was wrong, and that applies to
            signed-out visitors' clicks too. Nothing in them says who you were.
        </p>
        <p>
            Your analysis photo needs no deletion request, because it was never
            stored. The copy on the results screen is on your device, and deleting
            your account clears it.
        </p>
        <p>
            If you would rather a person did it, or you cannot sign in,
            <a href="mailto:privacy@skingenie.ai">privacy@skingenie.ai</a> still
            reaches us and is still answered.
        </p>

        <h2>Children</h2>
        <p>
            SkinGenie is not for children and is not directed at anyone under 13. Do
            not upload a photograph of a child.
        </p>

        <h2>Changes</h2>
        <p>
            If what we do with your data changes, this page changes with it, and the
            date at the top says when.
        </p>

        <h2>Contact</h2>
        <p>
            <a href="mailto:privacy@skingenie.ai">privacy@skingenie.ai</a>
        </p>
    </article>
</div>

<style>
    .eyebrow {
        font-size: var(--text-xs);
        letter-spacing: var(--track-wide);
        text-transform: uppercase;
        color: var(--text-muted);
        margin-bottom: var(--space-2);
    }

    .updated {
        color: var(--text-muted);
        font-size: var(--text-sm);
        margin-top: calc(var(--space-2) * -1);
    }

    .lead {
        font-size: var(--text-lg);
        line-height: var(--leading-relaxed);
    }

    h2 {
        margin-top: var(--space-10);
    }
</style>
